import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LayoutDashboard, LogOut } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { TierBadge } from '@/components/common/TierBadge'

function getInitials(email?: string): string {
  if (!email) return '?'
  const name = email.split('@')[0]
  return name.substring(0, 2).toUpperCase()
}

function getAvatarColor(email?: string): string {
  if (!email) return 'bg-gray-400'
  const colors = [
    'bg-primary-500',
    'bg-purple-500',
    'bg-green-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
  ]
  const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const initials = getInitials(user?.email)
  const avatarColor = getAvatarColor(user?.email)

  return (
    <div className="relative" ref={menuRef} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor} ${
            user?.tier !== 'free' ? 'ring-2 ring-amber-400' : ''
          }`}
        >
          {initials}
        </div>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 transition-all" role="menu">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${avatarColor}`}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            {user?.tier && (
              <div className="mt-2">
                <TierBadge tier={user.tier} />
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              role="menuitem"
            >
              <LayoutDashboard className="w-4 h-4 text-gray-400" />
              Dashboard
            </Link>
          </div>

          {/* Sign out */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => {
                logout()
                setOpen(false)
              }}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
              role="menuitem"
            >
              <LogOut className="w-4 h-4 text-gray-400" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
